import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { Table } from '@/components/application/table/table'
import { Select } from '@/components/ui/Select'
import { useSimSummary, useSimTags } from '@/hooks/api/useSimulations'
import { PassRateBar } from './PassRateBar'
import type { SimSummaryFilters } from '@/types/simulation'
import type { SortDescriptor } from 'react-aria-components'

/**
 * Summary tab - displays pass rates by assistant
 */
export function SimSummaryTab() {
  const { t } = useTranslation()

  // Filters
  const [filters, setFilters] = useState<SimSummaryFilters>({})
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'pass_rate',
    direction: 'descending',
  })

  // Fetch available tags
  const { data: availableTags = [] } = useSimTags()

  // Fetch summary data
  const { data: summaries = [], isLoading, isFetching } = useSimSummary({ filters })

  // Sort summaries client-side
  const sortedSummaries = [...summaries].sort((a, b) => {
    const column = sortDescriptor.column as keyof typeof a
    const aVal = a[column]
    const bVal = b[column]
    if (aVal === null || aVal === undefined) return 1
    if (bVal === null || bVal === undefined) return -1
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
    return sortDescriptor.direction === 'descending' ? -cmp : cmp
  })

  // Handlers
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  const handleTagFilter = useCallback((key: string | number | null) => {
    setFilters((prev) => ({
      ...prev,
      tag_ids: key ? [Number(key)] : undefined,
    }))
  }, [])

  // Tag options for filter
  const tagOptions = [
    { value: '', label: t('resources.generic.all') },
    ...availableTags.map((tag) => ({
      value: String(tag.id),
      label: tag.name,
    })),
  ]

  // Get currently selected tag ID for the select
  const selectedTagId = filters.tag_ids?.[0] ? String(filters.tag_ids[0]) : ''

  // Calculate totals
  const totalRuns = summaries.reduce((acc, s) => acc + s.total_runs, 0)
  const totalPassed = summaries.reduce((acc, s) => acc + s.passed, 0)
  const totalFailed = summaries.reduce((acc, s) => acc + s.failed, 0)
  const overallPassRate = totalRuns > 0 ? totalPassed / totalRuns : 0

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="w-48">
          <Select
            options={tagOptions}
            selectedKey={selectedTagId}
            onSelectionChange={handleTagFilter}
            placeholder={t('pages.simulations.filters.tag')}
            aria-label={t('pages.simulations.filters.tag')}
          />
        </div>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <StatCard
          label={t('pages.simulations.summary.total_runs')}
          value={totalRuns}
        />
        <StatCard
          label={t('pages.simulations.summary.passed')}
          value={totalPassed}
          variant="success"
        />
        <StatCard
          label={t('pages.simulations.summary.failed')}
          value={totalFailed}
          variant="error"
        />
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">
            {t('pages.simulations.summary.overall_pass_rate')}
          </div>
          <PassRateBar passRate={overallPassRate} size="lg" />
        </div>
      </div>

      {/* Loading bar */}
      {isFetching && (
        <div className="h-1 w-full overflow-hidden rounded bg-gray-100">
          <div className="h-full w-1/3 animate-pulse bg-brand-500" />
        </div>
      )}

      {/* Table */}
      {sortedSummaries.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          {t('pages.simulations.summary.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <Table
            aria-label={t('pages.simulations.summary.title')}
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
          >
            <Table.Header>
              <Table.Head id="assistant_name" label={t('pages.simulations.summary.columns.assistant')} allowsSorting isRowHeader />
              <Table.Head id="total_runs" label={t('pages.simulations.summary.columns.total_runs')} allowsSorting />
              <Table.Head id="passed" label={t('pages.simulations.summary.columns.passed')} allowsSorting />
              <Table.Head id="failed" label={t('pages.simulations.summary.columns.failed')} allowsSorting />
              <Table.Head id="pass_rate" label={t('pages.simulations.summary.columns.pass_rate')} allowsSorting />
            </Table.Header>

            <AriaTableBody>
              {sortedSummaries.map((summary) => (
                <Table.Row key={summary.assistant_id}>
                  <Table.Cell className="font-medium">{summary.assistant_name}</Table.Cell>
                  <Table.Cell>{summary.total_runs}</Table.Cell>
                  <Table.Cell className="text-success-600 dark:text-success-400">
                    {summary.passed}
                  </Table.Cell>
                  <Table.Cell className="text-error-600 dark:text-error-400">
                    {summary.failed}
                  </Table.Cell>
                  <Table.Cell className="w-48">
                    <PassRateBar passRate={summary.pass_rate} />
                  </Table.Cell>
                </Table.Row>
              ))}
            </AriaTableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

interface StatCardProps {
  label: string
  value: number
  variant?: 'default' | 'success' | 'error'
}

function StatCard({ label, value, variant = 'default' }: StatCardProps) {
  const valueColorClasses = {
    default: 'text-gray-900 dark:text-gray-100',
    success: 'text-success-600 dark:text-success-400',
    error: 'text-error-600 dark:text-error-400',
  }

  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${valueColorClasses[variant]}`}>
        {value}
      </div>
    </div>
  )
}

export default SimSummaryTab
